<template>
  <div class="avatar-container" :style="{ width: size + 'px' }" :title="title">
    <!-- Рамка аватарки с динамическим размером -->
    <div 
      class="avatar-preview" 
      :style="{ width: size + 'px', height: size + 'px' }" 
      @click="triggerFileInput"
    >
      <img v-if="avatarUrl" :src="avatarUrl" alt="Аватарка" class="avatar-image" />
      <div v-else class="avatar-placeholder">
        <!-- Скрываем текст, если рамка слишком мала для него -->
        <span v-if="size >= 60">Нет фото</span>
        <span v-else title="Нет фото">?</span>
      </div>
      
      <!-- Оверлей при наведении -->
      <div class="avatar-overlay">
        <span>{{ avatarUrl ? 'Изм.' : 'Загр.' }}</span>
      </div>
    </div>

    <!-- Кнопка удаления -->
    <button v-if="avatarUrl" type="button" class="delete-btn" @click="removeAvatar">
      Удалить
    </button>

    <!-- Скрытый инпут для выбора файла -->
    <input
      type="file"
      ref="fileInput"
      accept="image/*"
      class="hidden-input"
      @change="onFileChange"
    />
  </div>
</template>

<script>
export default {
  name: 'AvatarUploader',
  props: {
    // Размер стороны квадратной рамки в пикселях (по умолчанию 64px)
    size: {
      type: Number,
      default: 100
    },
    // Максимальный размер файла в мегабайтах (по умолчанию 2 МБ)
    maxSizeMb: {
      type: Number,
      default: 2
    },
    // Минимальная ширина изображения в пикселях
    minWidth: {
      type: Number,
      default: 150
    },
    // Минимальная высота изображения в пикселях
    minHeight: {
      type: Number,
      default: 150
    },
    title: {
      type: String,
      default: 'Фото профиля'
    }
  },
  data() {
    return {
      avatarUrl: null, // Локальная ссылка на изображение
    };
  },
  computed: {
    maxFileSizeInBytes() {
      return this.maxSizeMb * 1024 * 1024;
    }
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    async onFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        alert('Пожалуйста, выберите корректный файл изображения.');
        this.resetFileInput();
        return;
      }

      if (file.size > this.maxFileSizeInBytes) {
        alert(`Файл слишком большой! Максимальный размер — ${this.maxSizeMb} МБ.`);
        this.resetFileInput();
        return;
      }

      const tempUrl = URL.createObjectURL(file);

      try {
        await this.validateImageDimensions(tempUrl);
        
        this.revokeLocalUrl();
        this.avatarUrl = tempUrl;
        this.uploadAvatarToServer(file);

      } catch (error) {
        alert(error.message || 'Ошибка при валидации изображения.');
        URL.revokeObjectURL(tempUrl);
        this.resetFileInput();
      }
    },

    validateImageDimensions(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        
        img.onload = () => {
          if (img.width < this.minWidth || img.height < this.minHeight) {
            reject(new Error(
              `Разрешение изображения слишком маленькое!\n` +
              `Минимум: ${this.minWidth}x${this.minHeight} px.\n` +
              `Ваше фото: ${img.width}x${img.height} px.`
            ));
          } else {
            resolve();
          }
        };

        img.onerror = () => {
          reject(new Error('Не удалось прочитать файл изображения.'));
        };

        img.src = url;
      });
    },

    removeAvatar() {
      if (confirm('Вы уверены, что хотите удалить аватарку?')) {
        this.revokeLocalUrl();
        this.avatarUrl = null;
        this.resetFileInput();
        this.deleteAvatarFromServer();
      }
    },

    resetFileInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    revokeLocalUrl() {
      if (this.avatarUrl && this.avatarUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.avatarUrl);
      }
    },

    // Заглушки для API
    uploadAvatarToServer(file) {
      console.log('Файл отправляется на сервер:', file);
    },
    deleteAvatarFromServer() {
      console.log('Запрос на удаление аватарки с сервера');
    }
  },
  beforeDestroy() {
    this.revokeLocalUrl();
  }
};
</script>

<style scoped>
.avatar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

/* Базовые стили для классической квадратной рамки с контуром 1px цвета #0364c5 */
.avatar-preview {
  position: relative;
  border: 1px solid #0364c5;
  background-color: #f5f5f5;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  color: #757575;
  font-family: sans-serif;
  font-size: 12px;
  user-select: none;
  text-align: center;
  padding: 4px;
}

.avatar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  color: #ffffff;
  font-family: sans-serif;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.avatar-preview:hover .avatar-overlay {
  opacity: 1;
}

.delete-btn {
  background: none;
  border: none;
  color: #d32f2f;
  font-family: sans-serif;
  font-size: 11px;
  cursor: pointer;
  padding: 2px 4px;
  text-decoration: underline;
  white-space: nowrap;
}

.delete-btn:hover {
  color: #b71c1c;
}

.hidden-input {
  display: none;
}
</style>